import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { listReports, dismissReportsForContent, unifiedDeleteDoc, type ContentReport, type ReportContentType } from '@watchlist/shared';
import { font } from '../../utils/fontHelper';
import { useToast } from '../utils/ToastProvider';
import { setUserBlocked } from '../../services/functionsApi';
import { deleteCrewPost } from '../../services/crewPostService';
import { formatFirebaseDate } from '../../utils/dateUtils';

/** Aggregated row: one entry per unique reported contentId, with all reporters grouped. */
interface ReportedPostRow {
  contentId: string;
  contentType: ReportContentType;
  contentLabel: string | null;
  contentAuthorUid: string | null;
  reporterUids: string[];
  lastReportedAt: string;
}

const contentTypeLabel = (t: ReportContentType): string => {
  switch (t) {
    case 'review':   return 'Review';
    case 'crewPost': return 'Crew post';
    case 'bundle':   return 'Activity';
    case 'user':     return 'User profile';
    default:         return 'Content';
  }
};

const ReportedPostsPanel: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [rows, setRows] = useState<ReportedPostRow[]>([]);
  const [loading, setLoading] = useState(true);
  /** contentId currently being acted on — disables that row's buttons. */
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reports: ContentReport[] = await listReports(200);

      // Group by contentId, aggregating reporters and tracking most-recent report date.
      const byContent = new Map<string, ReportedPostRow>();
      for (const r of reports) {
        const existing = byContent.get(r.contentId);
        if (existing) {
          if (!existing.reporterUids.includes(r.reporterUid)) {
            existing.reporterUids.push(r.reporterUid);
          }
          if (r.reportedAt > existing.lastReportedAt) {
            existing.lastReportedAt = r.reportedAt;
          }
        } else {
          byContent.set(r.contentId, {
            contentId: r.contentId,
            contentType: r.contentType,
            contentLabel: r.contentLabel,
            contentAuthorUid: r.contentAuthorUid,
            reporterUids: [r.reporterUid],
            lastReportedAt: r.reportedAt,
          });
        }
      }

      const list = Array.from(byContent.values()).sort(
        (a, b) => (a.lastReportedAt < b.lastReportedAt ? 1 : -1)
      );
      setRows(list);
    } catch (e) {
      console.error('ReportedPostsPanel: load failed', e);
      showError('Could not load reports.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDeleteContent = async (row: ReportedPostRow) => {
    setBusyId(row.contentId);
    try {
      if (row.contentType === 'crewPost') {
        await deleteCrewPost(row.contentId);
        window.dispatchEvent(new CustomEvent('crewPostDeleted', { detail: { postDocId: row.contentId } }));
      } else if (row.contentType === 'review') {
        await unifiedDeleteDoc('userMovies', row.contentId);
      } else if (row.contentType === 'bundle') {
        await unifiedDeleteDoc('socialBundles', row.contentId);
        window.dispatchEvent(new CustomEvent('crewBundleDeleted', { detail: { bundleId: row.contentId } }));
      }
      await dismissReportsForContent(row.contentId);
      showSuccess('Content deleted and reports cleared.');
      setRows(prev => prev.filter(r => r.contentId !== row.contentId));
    } catch (e) {
      console.error('ReportedPostsPanel: delete failed', e);
      showError('Could not delete content.');
    } finally {
      setBusyId(null);
    }
  };

  const handleBlockAuthor = async (row: ReportedPostRow) => {
    if (!row.contentAuthorUid) return;
    setBusyId(row.contentId);
    try {
      await setUserBlocked(row.contentAuthorUid, true);
      showSuccess(`Blocked user.`);
    } catch (e) {
      console.error('ReportedPostsPanel: block failed', e);
      showError('Could not block the user.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (row: ReportedPostRow) => {
    setBusyId(row.contentId);
    try {
      await dismissReportsForContent(row.contentId);
      showSuccess('Reports dismissed.');
      setRows(prev => prev.filter(r => r.contentId !== row.contentId));
    } catch (e) {
      console.error('ReportedPostsPanel: dismiss failed', e);
      showError('Could not dismiss reports.');
    } finally {
      setBusyId(null);
    }
  };

  const totalCount = useMemo(() => rows.length, [rows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (totalCount === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: font(), color: theme.palette.text.secondary }}>
          No reported posts or users. The queue is clear.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontFamily: font(), color: theme.palette.text.secondary, fontSize: 14, mb: 2 }}>
        {totalCount} item{totalCount === 1 ? '' : 's'} flagged by users. Take action or dismiss the report.
      </Typography>

      <Stack divider={<Divider />} spacing={0}>
        {rows.map(row => {
          const busy = busyId === row.contentId;
          const canDelete = row.contentType === 'crewPost' || row.contentType === 'review' || row.contentType === 'bundle';
          return (
            <Box key={row.contentId} sx={{ py: 2 }}>
              {/* Type + report count + timestamp */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Chip
                  label={contentTypeLabel(row.contentType)}
                  size="small"
                  sx={{ fontFamily: font(), fontSize: 11, fontWeight: 600 }}
                />
                <Chip
                  label={`${row.reporterUids.length} report${row.reporterUids.length === 1 ? '' : 's'}`}
                  size="small"
                  color="warning"
                  sx={{ fontFamily: font(), fontSize: 11, fontWeight: 600 }}
                />
                <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary }}>
                  {formatFirebaseDate(row.lastReportedAt, '', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </Typography>
              </Stack>

              {/* Label (username / post title) */}
              {row.contentLabel && (
                <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 14, color: theme.palette.text.primary, mb: 0.5 }}>
                  {row.contentLabel}
                </Typography>
              )}

              {/* Content ID */}
              <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary, mb: 1 }}>
                ID: <code>{row.contentId}</code>
              </Typography>

              {/* View content / author links */}
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {(row.contentType === 'crewPost' || row.contentType === 'bundle') && (
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<OpenInNew fontSize="small" />}
                    onClick={() => {
                      if (row.contentType === 'crewPost') {
                        navigate(`/?postId=${encodeURIComponent(row.contentId)}`);
                      } else {
                        navigate(`/?bundleId=${encodeURIComponent(row.contentId)}`);
                      }
                    }}
                    sx={{ fontFamily: font(), textTransform: 'none', fontSize: 13, p: 0, minWidth: 0 }}
                  >
                    View {row.contentType === 'crewPost' ? 'post' : 'activity'}
                  </Button>
                )}
                {row.contentAuthorUid && (
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<OpenInNew fontSize="small" />}
                    onClick={() => navigate(`/profile/${row.contentAuthorUid}`)}
                    sx={{ fontFamily: font(), textTransform: 'none', fontSize: 13, p: 0, minWidth: 0 }}
                  >
                    View author profile
                  </Button>
                )}
              </Stack>

              {/* Actions */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {canDelete && (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => handleDeleteContent(row)}
                    disabled={busy}
                    sx={{ fontFamily: font(), textTransform: 'none' }}
                  >
                    {row.contentType === 'review' ? 'Delete review' : row.contentType === 'bundle' ? 'Delete activity' : 'Delete post'}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => handleBlockAuthor(row)}
                  disabled={busy || !row.contentAuthorUid}
                  sx={{ fontFamily: font(), textTransform: 'none' }}
                >
                  Block author
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleDismiss(row)}
                  disabled={busy}
                  sx={{ fontFamily: font(), textTransform: 'none' }}
                >
                  Dismiss reports
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ReportedPostsPanel;
